import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, useDisclosure} from '@chakra-ui/react'
import {ErrorModal} from '../modals/error-modal'
import {
    useAccessToken,
    useCommerceApi,
    useCustomerId,
    useShopperBasketsMutationHelper,
    useShopperCustomersMutation,
} from '@salesforce/commerce-sdk-react'
import {
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST,
} from '@salesforce/retail-react-app/app/constants'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceData} from '../../utils/product-utils'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
// import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useToast} from '../../hooks/use-toast'
import {
    ADD_TO_CART_ERROR_KEYS,
    ADD_TO_CART_ERROR_MESSAGES,
    API_ERROR_MESSAGE,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
} from '../../constants'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useWishList} from '../../hooks/use-wish-list'

const RecommendedPersonalizationProducts = () => {
    const intl = useIntl()
    const {addLayer} = useGoogleTagManager()
    const {currency} = useCurrency()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {data: basket} = useCurrentBasket()
    const showToast = useToast()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const location = useLocation()
    const toast = useToast()
    const customerId = useCustomerId()
    const navigate = useNavigation()
    const {locale} = useMultiSite()
    const [showErrorMessage, setShowErrorMessage] = useState(0)
    const [retrievedProduct, setRetrievedProduct] = useState(null)
    const {formatMessage} = useIntl()
    const {
        isOpen: isErrorModalOpen,
        onOpen: onErrorModalOpen,
        onClose: onErrorModalClose,
    } = useDisclosure()

    const showError = () => {
        showToast({
            title: intl.formatMessage(API_ERROR_MESSAGE),
            status: 'error',
        })
    }

    const {data: wishlist} = useWishList()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem',
    )

    const handleAddToWishlist = (product, variant, quantity) => {
        const isItemInWishlist = wishlist?.customerProductListItems?.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id,
        )

        if (!isItemInWishlist) {
            createCustomerProductListItem.mutate(
                {
                    parameters: {
                        listId: wishlist.id,
                        customerId,
                    },
                    body: {
                        // NOTE: API does not respect quantity, it always adds 1
                        quantity,
                        productId: variant?.productId || product?.id,
                        public: true,
                        priority: 1,
                        type: 'product',
                    },
                },
                {
                    onSuccess: () => {
                        toast({
                            title: intl.formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {
                                quantity: 1,
                            }),
                            status: 'success',
                        })
                    },
                    onError: () => {
                        showError()
                    },
                },
            )
        } else {
            toast({
                title: intl.formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                status: 'info',
                action: (
                    <Button variant="link" onClick={() => navigate('/account/wishlist')}>
                        {intl.formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                    </Button>
                ),
            })
        }
    }

    const addToCart = async (productSelectionValues) => {
        try {
            const productItems = productSelectionValues.map(({variant, quantity, product}) => ({
                productId: variant?.productId,
                price: variant?.price,
                quantity,
                c_MaterialGroup: product?.c_materialGroup,
                c_categoryName: product?.c_productCategory,
                c_subModelIdentifier: product?.c_subModelIdentifier,
                c_prdhaCategory: product?.c_prdhaCategory,
            }))
            await addItemToNewOrExistingBasket(productItems)

            // einstein.sendAddToCart(productItems)

            return productSelectionValues
        } catch (error) {
            console.error('error', error)
            showError(error)
        }
    }
    const {
        isOpen: isAddToCartModalOpen,
        onOpen: onAddToCartModalOpen,
        onClose: onAddToCartModalClose,
    } = useAddToCartModalContext()

    useEffect(() => {
        if (isAddToCartModalOpen) {
            onAddToCartModalClose()
        }
    }, [location.pathname])

    const validateOrderability = (variants, quantity, stockLevel) =>
        quantity > 0 && quantity <= stockLevel

    const validateAndShowError = () => {
        let hasValidSelection = true

        const {variants, inventory} = retrievedProduct
        const stockLevel = inventory?.stockLevel

        const isValid = validateOrderability(variants, 1, stockLevel)
        if (!isValid) {
            hasValidSelection = false
            if (!variants) {
                setShowErrorMessage(ADD_TO_CART_ERROR_KEYS.NO_VARIANT_SELECTED)
            } else if (!stockLevel || stockLevel <= 0) {
                setShowErrorMessage(ADD_TO_CART_ERROR_KEYS.OUT_OF_STOCK_FOR_PRODUCT)
            } else {
                setShowErrorMessage(ADD_TO_CART_ERROR_KEYS.INVENTORY_REMAINING_FOR_PRODUCT)
            }
        }

        if (!hasValidSelection) {
            onErrorModalOpen()
        }

        return hasValidSelection
    }

    const handleCartItem = async (productData) => {
        // check if there is already a customized product in the cart
        if (basket?.productItems?.[0]?.c_officinaConfigurationId) {
            return showToast({
                title: intl.formatMessage({
                    defaultMessage:
                        'Since you are adding a customized product to your cart, your order cannot include other types of products. Before adding other products to your cart, complete your order.',
                    id: 'officina.error.complete_your_order',
                }),
                status: 'error',
            })
        }

        const hasValidSelection = validateAndShowError()
        if (!hasValidSelection) {
            return null
        }

        if (!addToCart) return null

        try {
            const itemsAdded = await addToCart(
                productData.map((product) => ({
                    product: product,
                    variant: product?.c_variants?.[0] || product?.variants?.[0],
                    quantity: 1,
                })),
            )

            // Open modal only when `addToCart` returns some data
            // It's possible that the item has been added to cart, but we don't want to open the modal.
            // See wishlist_primary_action for example.
            if (itemsAdded) {
                // If the products have been added successfully, show the confirmation modal
                onAddToCartModalOpen({
                    itemsAdded,
                    selectedQuantity: 1,
                    isBundle: false,
                })
                await addLayer(polish_ecommerce_gtm_event.bind(this))
                await addLayer(
                    item_gtm_event.bind(this, {
                        event: 'add_to_cart',
                        currency: currency,
                        bundle: 'false',
                        items: [
                            {
                                item_name: retrievedProduct?.c_defaultName,
                                item_id:
                                    retrievedProduct?.c_variants?.[0].productId ||
                                    retrievedProduct?.variants?.[0].productId,
                                item_category: retrievedProduct?.c_defaultCategoryName,
                                item_variant: retrievedProduct?.id,
                                price: parseFloat(
                                    (
                                        retrievedProduct?.price /
                                        (1 + parseFloat(retrievedProduct?.c_taxRate) / 100)
                                    ).toFixed(2),
                                ),
                                fullprice: getPriceData(retrievedProduct)?.isOnSale
                                    ? 'reduced'
                                    : 'fullprice',
                                rating_points: '', //?Value undecided or missing
                                erp_category:
                                    retrievedProduct?.c_MaterialGroup ||
                                    retrievedProduct?.c_materialGroup,
                                prdha_category:
                                    retrievedProduct?.c_prdhaCategory ||
                                    retrievedProduct?.c_prdha_category,
                                quantity: 1,
                            },
                        ],
                    }),
                )
            }
        } catch (e) {
            showError() // Show error if there is a problem
        }
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const buttons = document.querySelectorAll(
                '#pdp_product_recommendation_fbt #js-del-plp-wrapper div.del-product-box__cta-wrapper button[data-variation-group], #cart_product_recommendations #js-del-plp-wrapper div.del-product-box__cta-wrapper button[data-variation-group]',
            )

            const handleClick = async (event) => {
                try {
                    const button = event.currentTarget
                    const sku = button.getAttribute('data-variation-group')
                    const token = await getTokenWhenReady()
                    const _retrievedProduct = await api.shopperProducts.getProduct({
                        parameters: {id: sku},
                        headers: {Authorization: `Bearer ${token}`},
                    })
                    setRetrievedProduct(_retrievedProduct)
                } catch (e) {
                    console.error('Error retrieving product:', e)
                }
            }
            buttons.forEach((button) => {
                button.addEventListener('click', handleClick, {once: true})
            })

            return () => {
                buttons.forEach((button) => {
                    button.removeEventListener('click', handleClick)
                })
            }
        }
    }, [api, getTokenWhenReady, handleCartItem])

    useEffect(() => {
        if (retrievedProduct) handleCartItem([retrievedProduct])
    }, [retrievedProduct])

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const wishlistButtons = document.querySelectorAll(
                '#pdp_product_recommendation_fbt #js-del-plp-wrapper div.del-product-box__image-wrapper button[data-variation-group], #cart_product_recommendations #js-del-plp-wrapper div.del-product-box__image-wrapper button[data-variation-group], #plp_product_recommentations #js-del-plp-wrapper div.del-product-box__image-wrapper button[data-variation-group], #homepage #js-del-plp-wrapper div.del-product-box__image-wrapper button[data-variation-group]',
            )

            const handleWishlistClick = async (event) => {
                try {
                    const button = event.currentTarget
                    const sku = button.getAttribute('data-variation-group')
                    const token = await getTokenWhenReady()
                    const _retrievedProduct = await api.shopperProducts.getProduct({
                        parameters: {id: sku},
                        headers: {Authorization: `Bearer ${token}`},
                        perPricebook: true,
                        expand: [
                            'availability',
                            'promotions',
                            'options',
                            'images',
                            'prices',
                            'set_products',
                            'bundled_products',
                            'recommendations',
                        ],
                        locale: locale.id,
                        currency,
                    })

                    const variant =
                        _retrievedProduct?.c_variants?.[0] || _retrievedProduct?.variants?.[0]
                    handleAddToWishlist(_retrievedProduct, variant, 1)
                } catch (e) {
                    console.error('Error adding to wishlist:', e)
                }
            }

            wishlistButtons.forEach((button) => {
                button.addEventListener('click', handleWishlistClick, {once: true})
            })

            return () => {
                wishlistButtons.forEach((button) => {
                    button.removeEventListener('click', handleWishlistClick)
                })
            }
        }
    }, [api, getTokenWhenReady, handleAddToWishlist])

    useEffect(() => {
        const wishlistButtonsYmal = document.querySelectorAll(
            '#pdp_product_recommendations_ymal #js-del-plp-wrapper div.del-product-box__image-wrapper button[data-variation-group]',
        )

        const handleWishlistClickYmal = async (event) => {
            try {
                const button = event.currentTarget
                const sku = button.getAttribute('data-variation-group')
                const token = await getTokenWhenReady()
                const _retrievedProductYmal = await api.shopperProducts.getProduct({
                    parameters: {id: sku},
                    headers: {Authorization: `Bearer ${token}`},
                    perPricebook: true,
                    expand: [
                        'availability',
                        'promotions',
                        'options',
                        'images',
                        'prices',
                        'set_products',
                        'bundled_products',
                        'recommendations',
                    ],
                    locale: locale.id,
                    currency,
                })
                const variant =
                    _retrievedProductYmal?.c_variants?.[0] || _retrievedProductYmal?.variants?.[0]
                handleAddToWishlist(_retrievedProductYmal, variant, 1)
            } catch (e) {
                console.error('Error adding to wishlist:', e)
            }
        }

        wishlistButtonsYmal.forEach((button) => {
            button.addEventListener('click', handleWishlistClickYmal, {once: true})
        })

        return () => {
            wishlistButtonsYmal.forEach((button) => {
                button.removeEventListener('click', handleWishlistClickYmal)
            })
        }
    }, [api, getTokenWhenReady, handleAddToWishlist])

    const gtmFiredRef = useRef(false)

    useEffect(() => {
        return () => {
            gtmFiredRef.current = false
        }
    }, [location.pathname])

    useEffect(() => {
        if (!gtmFiredRef.current) {
            const productIdNode = document.querySelectorAll('.del-product-box__code')
            const productIdsArray = Array.from(productIdNode)?.map((p) => p?.textContent.trim())
            const trimmedProducts = productIdsArray?.map((id) => id?.split(' ')[0])
            const joinedProductsStr = trimmedProducts?.join(',')
            if (joinedProductsStr) {
                ;(async () => {
                    try {
                        const token = await getTokenWhenReady()
                        const gtmProducts = await api.shopperProducts.getProducts({
                            parameters: {ids: joinedProductsStr},
                            headers: {Authorization: `Bearer ${token}`},
                            perPricebook: true,
                            expand: ['availability', 'images'],
                            locale: locale.id,
                            currency,
                        })
                        await addLayer(
                            item_gtm_event.bind(this, {
                                event: 'view_item_list',
                                item_list_name: 'carousel_plp',
                                items: gtmProducts?.data?.map((product) => ({
                                    item_name: product.c_defaultName,
                                    item_id: product.id,
                                    item_category: product.c_defaultCategoryName,
                                    item_variant:
                                        product?.c_variants?.[0]?.productId ||
                                        product?.variants?.[0]?.productId ||
                                        product?.variant?.productId,
                                    price: parseFloat(
                                        (
                                            product?.c_variants?.[0].price /
                                            (1 + parseFloat(product.c_taxRate) / 100)
                                        ).toFixed(2),
                                    ),
                                    fullprice: getPriceData(product)?.isOnSale
                                        ? 'reduced'
                                        : 'fullprice',
                                    rating_points: '',
                                    erp_category:
                                        product.c_MaterialGroup || product?.c_materialGroup,
                                    prdha_category:
                                        product.c_prdhaCategory || product?.c_prdha_category,
                                })),
                            }),
                        )
                        gtmFiredRef.current = true // Set flag before firing
                    } catch (e) {
                        console.error('Error retrieving products:', e)
                        showError()
                    }
                })()
            }
        }
    }, [api, getTokenWhenReady])

    const productName = retrievedProduct?.name || retrievedProduct?.productName || ''

    const errorModalMessage = showErrorMessage
        ? showErrorMessage === ADD_TO_CART_ERROR_KEYS.OUT_OF_STOCK_FOR_PRODUCT
            ? formatMessage(ADD_TO_CART_ERROR_MESSAGES.OUT_OF_STOCK_FOR_PRODUCT, {productName})
            : showErrorMessage === ADD_TO_CART_ERROR_KEYS.INVENTORY_REMAINING_FOR_PRODUCT
            ? formatMessage(ADD_TO_CART_ERROR_MESSAGES.INVENTORY_REMAINING_FOR_PRODUCT, {
                  stockLevel: retrievedProduct?.inventory?.stockLevel || 0,
                  productName,
              })
            : formatMessage(ADD_TO_CART_ERROR_MESSAGES.NO_VARIANT_SELECTED)
        : null

    return (
        <ErrorModal
            isOpen={isErrorModalOpen}
            onClose={() => {
                onErrorModalClose()
                setShowErrorMessage(0)
            }}
            message={errorModalMessage}
        />
    )
}

RecommendedPersonalizationProducts.propTypes = {
    badgeDetails: PropTypes.array,
    variant: PropTypes.string,
    tileImageVariant: PropTypes.string,
    title: PropTypes.node,
    products: PropTypes.array,
    prefix: PropTypes.string,
    gtmCarouselName: PropTypes.string,
}

export default RecommendedPersonalizationProducts
